import { useCallback, useEffect, useState } from 'react';
import { usePrevious } from 'react-use';

import type { NodeT } from '../../types';
import { HighlightType, NodePath } from '../types/node';
import type { Relation, RelationMap } from '../types/relation';
import events, { CANVAS_CLICK } from '../utils/canvas/events/events';
import { getNodesInPath } from '../utils/node/getNodesInPath/getNodesInPath';

export enum HighlightMatch {
  Node = 'node',
  Connection = 'connection',
  Both = 'both',
}

export type HighlightNode = { match: HighlightMatch; index: number };
export type HighlightNodeMap = Record<Relation['id'], HighlightNode>;
export type HighlightNodeOverride = Omit<HighlightNode, 'index'>;
export type HighlightNodeOverrideMap = Record<
  Relation['id'],
  HighlightNodeOverride
>;

export type HighlightState = {
  type: HighlightType;
  nodes: HighlightNodeMap;
};

export type SetHighlightPath = (args: HighlightPathArgs) => void;

type HighlightProps = {
  relations: RelationMap;
  invalidNodes: NodeT['id'][];
};

type HighlightPathArgs = {
  type?: HighlightType;
  match?: HighlightMatch;
  overrides?: HighlightNodeOverrideMap;
} & (
  | {
      path: NodePath;
      only?: never;
    }
  | {
      path?: never;
      only: Relation['id'][];
    }
);

export const useHighlight = ({
  relations,
  invalidNodes,
}: HighlightProps): [HighlightState, SetHighlightPath] => {
  const [highlight, setHighlight] = useState<HighlightState>({
    nodes: {},
    type: HighlightType.NONE,
  });

  const setHighlightPath = useCallback(
    ({
      path,
      only,
      type = HighlightType.INFO,
      match = HighlightMatch.Both,
      overrides = {},
    }: HighlightPathArgs) => {
      const connectionsInPath =
        only || getNodesInPath(path as NodePath, relations, 'connections');
      const nodes = connectionsInPath.reduce<HighlightNodeMap>(
        (nodes, id, index) => {
          const existingNode = overrides[id] as
            | HighlightNodeOverride
            | undefined;

          nodes[id] = { match, ...existingNode, index };

          return nodes;
        },
        {},
      );

      setHighlight({ nodes, type });
    },
    [setHighlight, relations],
  );

  const prevInvalidNodes = usePrevious(invalidNodes);

  useEffect(() => {
    const [nodeId] = invalidNodes;
    const isPreviouslyNodesSelected =
      prevInvalidNodes && prevInvalidNodes.length > 0;

    if (invalidNodes.length === 0 && isPreviouslyNodesSelected) {
      setHighlight({ nodes: {}, type: HighlightType.NONE });
    }

    if (nodeId) {
      setHighlightPath({ path: [null, nodeId], type: HighlightType.ERROR });
    }
  }, [invalidNodes, prevInvalidNodes, relations, setHighlightPath]);

  useEffect(() => {
    const listener = () => {
      if (invalidNodes.length > 0) {
        return;
      }

      setHighlight({ nodes: {}, type: HighlightType.NONE });
    };

    events.subscribe(CANVAS_CLICK, listener);

    return () => events.unsubscribe(listener);
  }, [invalidNodes, setHighlight]);

  return [highlight, setHighlightPath];
};
